import React from "react"
import { DI } from "@ui/DI"
import {
    Modal, ModalContent, ModalHeader, ModalHeaderTitle,
    ModalBody, ModalButtons, ModalSize
} from "@ui/common/Modal"
import { Button } from "@ui/common/buttons/Button"
import { LoadingIndicator } from "@ui/common/LoadingIndicator"
import { useBlocCoordinator } from "@lib/bloc/hooks"
import { AddExistingServiceItemsDialogViewModel } from "./AddExistingServiceItemsDialogViewModel"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"

type Props = {
    isOpen: boolean
    assetId: number | null
    alreadySelectedIds: number[] | null
    onClose: () => void
    onItemSelected: (item: ServiceItemForm) => void
}

export function AddExistingServiceItemsDialog({ isOpen, assetId, alreadySelectedIds, onClose, onItemSelected }: Props) {
    const [state, viewModel] = useBlocCoordinator(() => DI.addExistingServiceItemsDialogViewModel())
    const itemsAlreadyAdded = alreadySelectedIds ?? [];
    React.useEffect(() => {
        if (isOpen && assetId) {
            viewModel.load(assetId)
        }
    }, [isOpen, assetId])

    if (!isOpen) {
        return <Modal isVisible={false} />
    }

    return (
        <Modal isVisible={isOpen} useContentTag={true} onClose={onClose} size={ModalSize.Medium}>
            <ModalContent>
                <div className="add-existing-service-items">
                    <ModalHeader>
                        <ModalHeaderTitle>Select Existing Service Item</ModalHeaderTitle>
                    </ModalHeader>
                    <ModalBody>
                        {state.isLoading ? (
                            <LoadingIndicator isLoading />
                        ) : state.availableItems.length === 0 || state.availableItems.filter(i => !itemsAlreadyAdded.includes(i.id!)).length == 0 ? (
                            <p>No service items available</p>
                        ) : (
                            <ul className="add-existing-service-items-dialog__list">
                                {state.availableItems.filter(i => !itemsAlreadyAdded.includes(i.id!)).map((item) => (
                                    <li
                                        key={item.uiElementId}
                                        title={item.workOrderId ? "This Service Item is assigned to another Work Order" : ""}
                                        className={`add-existing-service-items-dialog__card ${item.workOrderId ? 'disabled' : ''}`}
                                        onClick={async () => {
                                            if (!item.workOrderId) {
                                                await viewModel.selectItem(item, onItemSelected)
                                                onClose()
                                            }
                                        }}
                                    >
                                        {/* Top row */}
                                        <div className="add-existing-service-items-dialog__row">
                                            <div>
                                                <span className="add-existing-service-items-dialog__service-id">
                                                    SERVICE #: {item.id?.toString().padStart(6, "0")}
                                                </span>
                                                {item.workOrderId && (
                                                    <span className="add-existing-service-items-dialog__work-order-id">
                                                        WORK ORDER #: {item.workOrderId.toString().padStart(6, "0")}
                                                    </span>
                                                )}
                                                <div className="add-existing-service-items-dialog__title">
                                                    {item.name || `Item #${item.uiElementId}`}
                                                </div>
                                            </div>
                                            <div className="add-existing-service-items-dialog__metrics">
                                                <div>
                                                    <div className="label">Due Hours</div>
                                                    <div className="value">{item.dueHourMeter ?? "-"}</div>
                                                </div>
                                                <div>
                                                    <div className="label">Due Miles</div>
                                                    <div className="value">{item.dueOdometer?.toLocaleString() ?? "-"}</div>
                                                </div>
                                                <div>
                                                    <div className="label">Due Date</div>
                                                    <div className="value">
                                                        {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "-"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bottom row */}
                                        <div className="add-existing-service-items-dialog__row details">
                                            <div className="field">
                                                <span className="label">Service Code: </span>
                                                <span className="value">{viewModel.getCode(item.serviceCode?.id)}</span>
                                            </div>
                                            <div className="field">
                                                <span className="label">Service Type: </span>
                                                <span className="value workRequestType">{item.workRequestType || "-"}</span>
                                            </div>
                                            <div className="notes">
                                                <span className="label">Notes: </span>
                                                <span className="value">{item.notes || "-"}</span>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </ModalBody>
                </div>
            </ModalContent>
        </Modal>
    )
}
